<template>

  <div class="alert alert-danger alert-dismissible" v-if="errorMessages.length > 0">
    <button type="button" class="close" @click="onDismissError" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
    <strong>Error:</strong>
    <ul>
      <li v-for="message in errorMessages">{{ message }}</li>
    </ul>
  </div>

  <div class="form-content">

    <div v-if="false">
      <input type="text" required v-model="formName" placeholder="Form name..." class="form-control input-lg form-name">
      <button @click.prevent="onCreateFormClicked" class="btn btn-print" v-if="!hasForm">Create</button>
    </div>

    <template v-if="hasForm">
      <div class="form-actions">
          <button class="btn btn-success" :disabled="!canPublish" @click.prevent="onPublishClicked">
            <span v-if="publishing"><i class="fas fa-spinner fa-spin"></i></span>
            Publish Changes
          </button>

          <div class="form-inline">
            <template v-if="siblingForms.length > 0">
              <button v-if="!uiShowCopyFieldsSelection" @click.stop="onShowCopyFieldsClicked" class="btn btn-default">
                <i class="fas fa-copy"></i> Copy Form
              </button>

              <div v-if="uiShowCopyFieldsSelection">
                <select
                  v-model="copyFromSourceFormId"
                  class="form-control"
                  >
                  <option value="">-- Select a Form --</option>
                  <option v-for="(option, index) in siblingForms" :key="option.id" :value="option.id">
                    {{ option.formName }}
                  </option>
                </select>

                <button @click.stop="onCopyFieldsClicked" class="btn btn-primary" :disabled="!copyFromSourceFormId" style="margin-left:5px;">
                  Copy
                </button>&nbsp;
                <button class="btn btn-default" @click.stop="onCancelCopyFieldsClicked">Cancel</button>
              </div>
            </template>
            <button class="btn btn-danger" @click.stop="onDeleteFormClicked" style="margin-left:5px;" v-if="!uiShowCopyFieldsSelection">
              <i class="fas fa-times"></i>
              Delete
            </button>
          </div>
      </div>

      <div v-if="activeFields.length === 0" class="alert alert-info">
        <i class="fas fa-info-circle"></i> No fields added yet. Click "Add Field" to get started.
      </div>

      <div class="form-fields" :class="{ 'is-dragging' : isDraggingField }">
        <div v-for="field in activeFields">
          <div class="form-field" 
            :class="{ 'dragging' : field.isDragging }"
            :draggable="true" 
            @dragstart="onFieldDragStart(field, $event)" 
            @dragover="onFieldDragOver(field, $event)"
            @dragend="onFieldDragEnd(field, $event)">

            <div class="form-field-move">
              <div class="form-field-move-handle">
                <i class="fas fa-ellipsis-v"></i>
              </div>
            </div>

            <div class="form-field-controls">
              <a class="form-field-action action-delete" @click.stop="onDeleteFieldClick(field)"><i class="fas fa-times"></i></a>
            </div>

            <div class="field-row">
              <div class="input-group input-group-lg">

                <input type="text" v-model="field.fieldName" placeholder="Name / Title" class="form-control" maxlength="100">

                <div class="input-group-btn">
                  <button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{{ getDataTypeName(field.dataType) }} <span class="caret"></span></button>
                  <ul class="dropdown-menu dropdown-menu-right">
                    <li><a href="#" @click="updateFieldDataType(field, 'TitleDescription')">Title / Description</a></li>
                    <li role="separator" class="divider"></li>
                    <li><a href="#" @click="updateFieldDataType(field, 'Text')">Text</a></li>
                    <li><a href="#" @click="updateFieldDataType(field, 'MultipleChoice')">Multiple Choice</a></li>
                    <li><a href="#" @click="updateFieldDataType(field, 'DateTime')">Date / Time</a></li>
                    <li><a href="#" @click="updateFieldDataType(field, 'File')">File Upload</a></li>
                  </ul>
                </div><!-- /btn-group --> 
              </div><!-- /input-group -->
            </div>

            <div class="field-row">
              <textarea v-model="field.description" placeholder="Description text" class="form-control" maxlength="2048" rows="3"></textarea>
            </div>

            <FieldText v-if="field.dataType === 'Text'" :editMode="editMode" :field="field" />
            <FieldDateTime v-if="field.dataType === 'DateTime'" :editMode="editMode" :field="field" />
            <FieldMultipleChoice v-if="field.dataType === 'MultipleChoice'" :editMode="editMode" :field="field" />
            <FieldFile v-if="field.dataType === 'File'" :editMode="editMode" :field="field" />
            <FieldTitleDescription v-if="field.dataType === 'TitleDescription'" :editMode="editMode" :field="field" />

          </div>
        </div>
        <button class="btn btn-primary btn-block" @click.prevent="onAddFieldClicked">Add Field <i class="fas fa-plus"></i></button>

      </div>
    </template>
  </div>



</template>
  
<script>
  import { toRaw } from 'vue';
  import formApi from '../../api/form-api.js';
  import FieldText from './Builder/TextField.vue';
  import FieldDateTime from './Builder/DateTimeField.vue';
  import FieldMultipleChoice from './Builder/MultipleChoiceField.vue';
  import FieldFile from './Builder/FileField.vue';
  import FieldTitleDescription from './Builder/TitleDescriptionField.vue';
  import _ from "lodash";


  // Properties that exist on the Field model that are only used on the client side
  const clientFieldProps = ['active', 'clientKey', 'isDragging'];

  export default {
    name: 'form-builder',

    components: {
      FieldText,
      FieldDateTime,
      FieldMultipleChoice,
      FieldFile,
      FieldTitleDescription
    },

    props: {
      formId : {
        type: Number,
        required: false
      },
      formType : {
        type: String,
        required: true
      }
    },

    data(){
      return {
        hasForm: false,
        formVersionId : null,
        formName : null,
        fields : null,
        dbFields : null,
        siblingForms : [],
        errorMessages : [],
        draggingField : null,
        editMode : true,
        loaded: false,
        publishing: false,
        isDraftVersion: false,
        saveTimeoutId: null,
        uiShowCopyFieldsSelection: false,
        copyFromSourceFormId : "" // Needs to be a string for select binding
      }
    },

    async mounted(){
      
      if(this.formId){
        const formResponse = await formApi.getForm(this.formId, true);
        this.setFormData(formResponse.data);
      }

      const formsOfTypeResponse = await formApi.getFormsOfType(this.formType);

      // Find list of sibling forms excluding ourselves and unpublished forms
      let siblingForms = formsOfTypeResponse.data.forms.filter(f => 
        f.publishedFormVersionId !== null && 
        f.id !== this.formId
      );

      this.siblingForms = siblingForms.sort((a, b) => a.formName.localeCompare(b.formName));

      this.loaded = true;
    },

    computed : {
      activeFields(){
        if(this.fields == null){
          return [];
        }

        return this.fields
          .filter(f => f.active)
          .sort((a, b) => a.displayOrdinal - b.displayOrdinal);
      },

      isDraggingField(){
        return this.draggingField != null;
      },

      canPublish(){
        if(this.publishing){
          return false;
        }
        
        if(!this.isDraftVersion){
          return false;
        }

        if(this.saveTimeoutId){
          return false;
        }

        return true;
      }
    },

    methods : {

      setFormData(form){
        if(form == null){
          this.hasForm = false;
          this.formName = "";
          this.formVersionId = null;
          this.fields = [];
          this.dbFields = [];
        }else{
          this.hasForm = true;
          this.formName = form.formName;
          this.formVersionId = form.formVersionId;
          this.isDraftVersion = form.isDraftVersion;

          // Backfilling values for fields loaded
          form.fields.forEach((f, idx) => {
            f.active = true;
            f.clientKey = crypto.randomUUID()
            f.displayOrdinal = f.ordinal;
          });

          this.fields = JSON.parse(JSON.stringify(form.fields));
          this.dbFields = JSON.parse(JSON.stringify(form.fields));
        }
        
      },

      async onCreateFormClicked(){
        // TODO: Implement form creation logic
        /*
        let postResult = null;
        
        try{
          postResult = await formApi.postCreateForm(this.formName, this.formType);
        }catch(ex){
          this.errorMessages.push(ex.response.data.message);
          return;
        }
        */
      },

      async onDeleteFormClicked(){
        if(!confirm("Are you sure you want to delete this form? This action cannot be undone.")){
          return;
        }

        try{
          await formApi.deleteForm(this.formId);

          this.setFormData(null);
          
        }catch(ex){
          this.errorMessages.push(ex.response.data.message);
        }
      },

      async onShowCopyFieldsClicked(){
        this.uiShowCopyFieldsSelection = true;
      },  

      async onCopyFieldsClicked(){
        
        formApi.postCopyFields(this.formId, this.copyFromSourceFormId)
          .then((response) => {
            this.setFormData(response.data);

            this.queueSaveChanges();
          })
          .catch((ex) => {
            this.errorMessages.push(ex.response.data.message);
          });

        this.uiShowCopyFieldsSelection = false;

      },

      onCancelCopyFieldsClicked(){
        this.uiShowCopyFieldsSelection = false;
        this.copyFromSourceFormId = ""; // Needs to be a string for select binding
      },

      onDismissError(){
        this.errorMessages.length = 0;
      },

      onAddFieldClicked(){

        const nextOrdinal = (this.fields.length + 1);

        let newField = this.newFieldModel();

        newField.ordinal = nextOrdinal;
        newField.displayOrdinal = nextOrdinal;

        this.fields.push(newField);
      },

      onDeleteFieldClick(field){
        this.fields = this.fields.filter(f => f.clientKey !== field.clientKey);
        this.queueSaveChanges();
      },

      async onPublishClicked(){
        this.publishing = true;

        try{
          await formApi.postPublishForm(this.formId, this.formVersionId);
        }catch(ex){
          this.errorMessages.push(ex.response.data.message);
        }

        this.publishing = false;
        this.isDraftVersion = false;
      },

      onFieldDragStart(field, evt){
        if(this.isInteractiveElement(document.activeElement)){
          evt.preventDefault();
          return;
        }

        field.isDragging = true;

        // Keep track of our dragged field value
        this.draggingField = field;

        this.isDragging = true;
      },

      onFieldDragOver(field, evt){
        evt.preventDefault();

        // If we're dragging over ourselves, ignore
        if(field == this.draggingField){
          return;
        }

        // Get the bounding rectangle of the target element
        const rect = evt.currentTarget.getBoundingClientRect();

        console.log(rect);

        // Calculate the mouse position relative to the element
        const mouseY = evt.clientY;
        const elementHeight = rect.height;

        // Calculate the relative position (0 to 1, where 0.5 is center)
        const relativePosition = (mouseY - rect.top) / elementHeight;

        console.log('Relative Position: ' + relativePosition);

        // Define the center zone (0.3 to 0.7 means middle 40%)
        // Only execute logic when drag is in this center zone
        const centerZoneStart = 0.3;
        const centerZoneEnd = 0.7;

        // Only proceed if we're in the center zone
        if (relativePosition < centerZoneStart || relativePosition > centerZoneEnd) {
          return;
        }

        let newOrdinal;

        // If we're dragging "down" in the list
        if(this.draggingField.displayOrdinal < field.displayOrdinal){
          newOrdinal = field.displayOrdinal + 0.5;
        }else if(this.draggingField.displayOrdinal > field.displayOrdinal){
          // If we're dragging "up" in the list
          newOrdinal = field.displayOrdinal - 0.5;
        }else{
          // We're over ourselves
          return;
        }

        this.draggingField.displayOrdinal = newOrdinal;
      },

      onFieldDragEnd(field, evt){

        this.isDragging = false;

        this.draggingField.isDragging = false;
        this.draggingField = null;

        // This is a sorted list which is an import distinction
        this.activeFields.forEach((f, idx) => {

          // We're a 1-based sort
          f.ordinal = (idx+1);
        });
      },

      updateFieldDataType(field, dataType){
        field.dataType = dataType;
      },

      getDataTypeName(dataType){
        switch(dataType){
          case 'Text':
            return 'Text';
          case 'DateTime':
            return 'Date / Time';
          case 'MultipleChoice':
            return 'Multiple Choice';
          case 'File':
            return 'File Upload';
          case 'TitleDescription':
            return 'Title / Description';
        }

        return dataType;
      },

      isInteractiveElement(element) {
        const interactiveElements = [
          'button',
          'input',
          'textarea',
          'select',
          'option',
        ];

        if (interactiveElements.includes(element.tagName.toLowerCase())) {
          return true;
        }

        return false;
      },

      queueSaveChanges: function() {

        this.isDraftVersion = true;

        if (this.saveTimeoutId) {
          console.log('clearing timeout');
          clearTimeout(this.saveTimeoutId);
          this.saveTimeoutId = null;
        }
        this.saveTimeoutId = setTimeout(() => {
          console.log('Saving changes');
          this.saveFieldChanges();
        }, 1500);
      },

      newFieldModel(){
        return {
            fieldName : null,
            description: null,
            dataType : 'Text',
            min : null,
            max : null,
            required : true,
            textRows : 1,
            ordinal : null,
            isDragging : false,
            active : true,
            clientKey : crypto.randomUUID()
          };
      },

      async saveFieldChanges(){

        let response = null;

        try{
          response = await formApi.postSaveChanges(this.formId, {
            formName : this.formName,
            formVersionId : this.formVersionId,
            fields : this.fields
          });
        }catch(ex){
          this.saveTimeoutId = null;
          this.errorMessages.push(ex.response.data.message);
          return;
        }

        this.saveTimeoutId = null;

        // If we got a new version id back, set it.
        this.formVersionId = response.data.formVersionId;

        let dbFields = [];
        
        response.data.fields.forEach(updatedField => {
          let dbField = this.dbFields.find(f => f.id == updatedField.id);

          if (!dbField) {
            dbField = updatedField;
          }

          // Remove properties that are client-side only
          // const { clientKey, ...cleanedDbField }= dbField;

          dbFields.push(dbField);
        });

        this.dbFields.length = 0;
        this.dbFields.push(...dbFields);

        // Sync only the server-assigned ID for new fields — never overwrite user's current edits.
        response.data.fields.forEach((updatedField) => {
          const localField = this.fields.find(f => f.clientKey === updatedField.clientKey);
          if (updatedField.id != null) {
            localField.id = updatedField.id;
          }
        });
      },
    },

    watch : {
      fields: {
        handler(newValue, oldValue) {
        
          // If this is an initialization, ignore it.
          if(oldValue == null){
            return;
          }

          var cleanedFields = this.fields.map(f => {
            // Create a copy of the field without the client-side only properties
            let cleanedField = { ...f };
            clientFieldProps.forEach(prop => delete cleanedField[prop]);
            return cleanedField;
          });

          var cleanedDbFields = this.dbFields.map(f => {
            // Create a copy of the field without the client-side only properties
            let cleanedField = { ...f };
            clientFieldProps.forEach(prop => delete cleanedField[prop]);
            return cleanedField;
          });

          if (!_.isEqual(cleanedFields, cleanedDbFields)) {
            this.queueSaveChanges();
          }
        },
        deep: true
      }
    }
  }
</script>

<style lang="scss">

  $form-field-padding: 15px;
  $form-content-padding: 10px;

  .form-name{
    margin-bottom:15px;
  }

  .form-content{
      padding:0px $form-content-padding;
  }

  .form-actions{
    display: flex;
    justify-content: space-between;
    padding: $form-field-padding;
    background-color: #dff0d8;
    margin-left: -$form-content-padding;
    margin-right: -$form-content-padding;
    margin-bottom:15px;
  }


  .form-fields{
    // border-collapse: collapse;
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none; /* IE 10+ and Edge */
    user-select: none; /* Standard syntax */

    input,select{
      -webkit-user-select:auto; /* Safari */
      -ms-user-select: auto; /* IE 10+ and Edge */
      user-select: auto; /* Standard syntax */
    }

    padding: $form-field-padding;

    .form-field{
      box-shadow:0px 4px 5px 0 #00000010;
      background-color: #f9f9f9;
      padding: 15px 20px;
      border: 1px solid #ddd;
      border-radius: 7px;
      position:relative;
      margin-top:30px;
      margin-bottom:10px;

      .field-row{
        margin-bottom:3px;
      }

      .fields-row{
        margin-bottom:3px;
        display: flex; 
        align-items: center; 
        gap: 15px;

        .field-set{
          display: flex; 
          align-items: center; 
          gap: 5px;
        }
      }

      .form-field-move{
        display: flex;
        position: absolute;
        left: -10px;
        top: 0;
        top: 3px;
        bottom: 0;
        font-size: 16px;
        color: #888;

        .form-field-move-handle{
          margin: auto 0;
          padding: 10px 7px;
          border: 1px solid #ddd;
          background-color: #fff;
          border-radius: 3px;
        }
      }

      .form-field-controls{
        // display:none;
        position:absolute;
        right:-10px;
        left:0;
        top:-15px;
        text-align: right;

        .form-field-action{
          background:#ffffff;
          border:1px solid #ddd;
          width:30px;
          height:30px;
          display:inline-block;
          border-radius: 50%;
          text-align: center;
          line-height:30px;
          cursor: pointer;

          &.action-delete{
            color:#a94442;

            &:hover{
              background:#d9534f;
              color:#fff;
            }
          }
        }
      }

      &:hover{
        .form-field-controls{
          // display:block;
        }
        
      }
    }

    .form-field[draggable=true]{
      cursor:all-scroll;

      &.dragging{
        border:1px dashed #aaa;

        .form-field-controls{
          display:none !important;
        }
      }
    }

    &.is-dragging{

      .form-field.dragging{
        .form-field-move-handle{
           background-color: var(--bs-color-primary);
           transition: background-color 1000ms linear;
           color:#fff;
        }
      }
      

      .form-field-controls{
        display:none !important;
      }

      .form-field:not(.dragging){
        opacity:0.3;

        .form-field-move-handle{
          display:none !important;
        }
      }
    }
    
  }
</style>